import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import Sidebar from './Sidebar';
import Header from './Header';
import ConfirmationModal from './ConfirmationModal';

interface Organization {
  id: number;
  name: string;
  slug: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  subscription_tier: string;
  max_users: number;
  max_students: number;
  max_institutions: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Organizations: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { isMobile } = useIsMobile();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    subscription_tier: 'basic',
    max_users: 100,
    max_students: 1000,
    max_institutions: 10
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data.organizations || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (org?: Organization) => {
    if (org) {
      setEditingOrg(org);
      setFormData({
        name: org.name,
        slug: org.slug,
        description: org.description || '',
        address: org.address || '',
        phone: org.phone || '',
        email: org.email || '',
        website: org.website || '',
        subscription_tier: org.subscription_tier,
        max_users: org.max_users,
        max_students: org.max_students,
        max_institutions: org.max_institutions
      });
    } else {
      setEditingOrg(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        subscription_tier: 'basic',
        max_users: 100,
        max_students: 1000,
        max_institutions: 10
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOrg(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingOrg) {
        await axios.put(
          `${API_BASE_URL}/api/organizations/${editingOrg.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/api/organizations`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      handleCloseDialog();
      fetchOrganizations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save organization');
    }
  };

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });

  const handleDelete = (id: number) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.id) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/organizations/${deleteDialog.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOrganizations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete organization');
    }
    setDeleteDialog({ open: false, id: null });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="content-area">
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h4" component="h1">
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('organizations.title')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {t('organizations.addOrganization')}
              </Button>
            </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {organizations.map((org) => (
            <Paper key={org.id} sx={{ p: 2, borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box minWidth={0} flex={1}>
                  <Typography variant="subtitle1" fontWeight="bold" noWrap>{org.name}</Typography>
                  {org.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{org.description}</Typography>
                  )}
                </Box>
                <Chip
                  label={org.is_active ? t('organizations.status.active') : t('organizations.status.inactive')}
                  color={org.is_active ? 'success' : 'default'}
                  size="small"
                  sx={{ ml: 1, flexShrink: 0 }}
                />
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                <Chip label={org.slug} size="small" variant="outlined" />
                <Chip
                  label={t(`organizations.subscriptionTiers.${org.subscription_tier}`)}
                  color={org.subscription_tier === 'enterprise' ? 'primary' : org.subscription_tier === 'premium' ? 'secondary' : 'default'}
                  size="small"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t('organizations.limits.users')}: {org.max_users} | {t('organizations.limits.students')}: {org.max_students} | {t('organizations.limits.institutions')}: {org.max_institutions}
              </Typography>
              <Box display="flex" justifyContent="flex-end" gap={0.5} mt={1}>
                <IconButton size="small" onClick={() => handleOpenDialog(org)} color="primary" aria-label="Edit organization">
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(org.id)} color="error" aria-label="Delete organization">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('organizations.table.name')}</TableCell>
                <TableCell>{t('organizations.table.slug')}</TableCell>
                <TableCell>{t('organizations.table.subscription')}</TableCell>
                <TableCell>{t('organizations.table.limits')}</TableCell>
                <TableCell>{t('organizations.table.status')}</TableCell>
                <TableCell>{t('organizations.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">{org.name}</Typography>
                    {org.description && (
                      <Typography variant="body2" color="text.secondary">{org.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell><Chip label={org.slug} size="small" /></TableCell>
                  <TableCell>
                    <Chip
                      label={t(`organizations.subscriptionTiers.${org.subscription_tier}`)}
                      color={org.subscription_tier === 'enterprise' ? 'primary' : org.subscription_tier === 'premium' ? 'secondary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{t('organizations.limits.users')}: {org.max_users}</Typography>
                    <Typography variant="body2">{t('organizations.limits.students')}: {org.max_students}</Typography>
                    <Typography variant="body2">{t('organizations.limits.institutions')}: {org.max_institutions}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={org.is_active ? t('organizations.status.active') : t('organizations.status.inactive')} color={org.is_active ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenDialog(org)} color="primary" aria-label="Edit organization"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(org.id)} color="error" aria-label="Delete organization"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingOrg ? t('organizations.editOrganization') : t('organizations.addOrganization')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('organizations.form.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('organizations.form.slug')}
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  required
                  fullWidth
                  helperText="URL-friendly identifier (lowercase, hyphens only)"
                />
              </Grid>
            </Grid>
            
            <TextField
              label={t('organizations.form.description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('organizations.form.subscriptionTier')} & {t('organizations.table.limits')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('organizations.form.subscriptionTier')}</InputLabel>
                    <Select
                      value={formData.subscription_tier}
                      onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
                      label={t('organizations.form.subscriptionTier')}
                    >
                      <MenuItem value="basic">{t('organizations.subscriptionTiers.basic')}</MenuItem>
                      <MenuItem value="premium">{t('organizations.subscriptionTiers.premium')}</MenuItem>
                      <MenuItem value="enterprise">{t('organizations.subscriptionTiers.enterprise')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('organizations.form.maxUsers')}
                    type="number"
                    value={formData.max_users}
                    onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('organizations.form.maxStudents')}
                    type="number"
                    value={formData.max_students}
                    onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('organizations.form.maxInstitutions')}
                    type="number"
                    value={formData.max_institutions}
                    onChange={(e) => setFormData({ ...formData, max_institutions: parseInt(e.target.value) })}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('organizations.form.contactInfo')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('organizations.form.email')}
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('organizations.form.phone')}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('organizations.form.website')}
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('organizations.form.address')}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    multiline
                    rows={2}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('modals.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingOrg ? t('organizations.buttons.update') : t('organizations.buttons.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        isOpen={deleteDialog.open}
        title={t('organizations.deleteTitle', 'Delete Organization')}
        message={t('organizations.deleteConfirm', 'Are you sure you want to delete this organization? This action cannot be undone.')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ open: false, id: null })}
      />
          </Box>
        </div>
      </div>
    </div>
  );
};

export default Organizations;

